import { useState } from "react";
import type { WallInput } from "@tasduvar/engine";
import { NumberField } from "../../ui/NumberField";
import { useHighlight } from "../../state/highlight";
import { toSvg, type DimLine, type Transform } from "./geometry";
import { applyPolygonDimension } from "./polygonDims";

/** Çizimdeki bir ölçünün yeni değerini duvar girdisine uygular (updateWall reçetesi). Anahtarlar `dimensionLines` ile aynı. */
export function applyDimension(_wall: WallInput, key: string, value: number): (w: WallInput) => void {
  return (w) => {
    const g = w.geometry;
    if (key === "Df") { g.embedmentDepth = Math.max(0, value); return; }
    if (g.mode === "rc-cantilever") {
      const v = Math.max(0.05, value);
      if (key === "H") { const H = g.stem.height + g.base.thickness; const k = value / H; g.stem.height = Math.round(g.stem.height * k * 1000) / 1000; g.base.thickness = Math.round(g.base.thickness * k * 1000) / 1000; }
      else if (key === "B") g.base.heelLength = Math.max(0, value - g.base.toeLength - g.stem.bottomThickness);
      else if (key === "rc.tt") g.stem.topThickness = Math.min(v, g.stem.bottomThickness);
      else if (key === "rc.tb") g.stem.bottomThickness = Math.max(v, g.stem.topThickness);
      else if (key === "rc.hs") g.stem.height = v;
      else if (key === "rc.tf") g.base.thickness = v;
      else if (key === "rc.toe") g.base.toeLength = Math.max(0, value);
      else if (key === "rc.heel") g.base.heelLength = Math.max(0, value);
      else if (key === "rc.key" && g.key) g.key.depth = v;
      return;
    }
    if (g.mode === "polygon") { g.points = applyPolygonDimension(g.points, key, value); return; }
    const last = g.steps[g.steps.length - 1]!;
    if (key === "H") { const H = g.steps.reduce((s, st) => s + st.height, 0) + (g.foundation?.height ?? 0); const k = value / H; for (const st of g.steps) st.height = Math.round(st.height * k * 1000) / 1000; if (g.foundation) g.foundation.height = Math.round(g.foundation.height * k * 1000) / 1000; return; }
    if (key === "B") { const toe = g.foundation?.toeExtension ?? 0, heel = g.foundation?.heelExtension ?? 0; last.bottomThickness = Math.max(0.05, value - toe - heel); return; }
    if (key === "top") { g.steps[0]!.topThickness = Math.max(0.05, value); return; }
    if (key === "bs") {   // eğik taban: topuk düşüşü Δ → tanα = Δ/B (0…0,3)
      const B = last.bottomThickness + (g.foundation?.toeExtension ?? 0) + (g.foundation?.heelExtension ?? 0);
      const s = Math.round(Math.min(0.3, Math.max(0, value / B)) * 1000) / 1000;
      g.baseSlope = s > 0 ? s : undefined;
      return;
    }
    const m = /^s(\d+)\.(h|t|b)$/.exec(key);
    if (m) {
      const st = g.steps[Number(m[1])]; if (!st) return;
      const v = Math.max(0.05, value);
      if (m[2] === "h") st.height = v; else if (m[2] === "t") st.topThickness = v; else st.bottomThickness = v;
      return;
    }
    if (!g.foundation) return;
    if (key === "f.h") g.foundation.height = Math.max(0.05, value);
    if (key === "f.toe") g.foundation.toeExtension = Math.max(0, value);
    if (key === "f.heel") g.foundation.heelExtension = Math.max(0, value);
  };
}

/** Tıklanabilir ölçü etiketi: tıkla → yerinde sayı alanı; odak/vurgu geometri panelindeki alanla ortak (useHighlight.dimKey). */
export function ActiveDimension({ t, dim, onCommit }: { t: Transform; dim: DimLine; onCommit: (v: number) => void }): JSX.Element {
  const [editing, setEditing] = useState(false);
  const { dimKey, setDim } = useHighlight();
  const hot = dimKey === dim.key;
  const vertical = Math.abs(dim.a.x - dim.b.x) < 1e-9;
  const a = toSvg(t, dim.a), b = toSvg(t, dim.b);
  const off = dim.offset * t.scale;
  const mx = vertical ? a.x + off : (a.x + b.x) / 2, my = vertical ? (a.y + b.y) / 2 : a.y - off;
  const value = vertical ? Math.abs(dim.b.y - dim.a.y) : Math.abs(dim.b.x - dim.a.x);
  // Dar yatay ölçüde etiket çizginin altına iner (Dimensions katmanıyla aynı kural)
  const tight = !vertical && dim.label.length * 6.2 > Math.abs(b.x - a.x) - 4;
  if (!editing) {
    return (
      <foreignObject x={vertical ? mx - 84 : mx - 40} y={tight ? my + 1 : my - 11} width={80} height={22} data-dim-edit={dim.key} data-hot={hot || undefined}>
        <div className={`flex ${vertical ? "justify-end" : "justify-center"}`}>
          <button type="button" onClick={() => { setDim(dim.key); setEditing(true); }} onMouseEnter={() => setDim(dim.key)} onMouseLeave={() => { if (!editing) setDim(null); }}
            className={`text-[10px] leading-4 num px-1 rounded whitespace-nowrap cursor-text border transition-colors duration-150 ${hot ? "bg-soil text-sheet border-soil font-medium" : "bg-sheet/90 text-ink border-transparent underline decoration-dotted decoration-stone hover:border-rule"}`}
            aria-label={`${dim.label} düzenle`}>{dim.label}</button>
        </div>
      </foreignObject>
    );
  }
  return (
    <foreignObject x={mx - 56} y={my - 16} width={112} height={40} data-dim-edit={dim.key}>
      <div className="flex items-center gap-1 bg-sheet rounded border border-soil shadow-sheet p-1" onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Enter") { setEditing(false); setDim(null); } }} onBlur={() => { setEditing(false); setDim(null); }}>
        <span className="text-[10px] num text-stone pl-1">{dim.key}</span>
        <NumberField id={`dim-${dim.key}`} label={`${dim.key} (m)`} value={value} onChange={(v) => onCommit(v)} min={key0(dim.key)} digits={2} autoFocus hideLabel />
      </div>
    </foreignObject>
  );
}
const key0 = (k: string): number => (k === "Df" || k === "bs" || k === "f.toe" || k === "f.heel" || k === "rc.toe" || k === "rc.heel" || /^e\d+\.x$/.test(k) ? 0 : 0.05);
